<script setup lang="ts">
import { Ref, ref } from "vue";
import { useApiClient } from "@/api/client";

import { useI18n } from "vue-i18n";
import { toastApiResult } from "@/toaster";
import RoleListViewDTO = App.Data.RoleListViewDTO;
import { debounce } from "vue-debounce";
import useDialog from "@/ui/composables/useDialog";
const { t } = useI18n();

type HeaderType = {
	title: string;
	key: string;
	align?: "start" | "end";
	sortable?: boolean;
};

const headers: HeaderType[] = [
	{
		title: t("roles.overview.headerName"),
		align: "start",
		key: "name",
	},
	{
		title: t("roles.overview.headerActions"),
		key: "actions",
		align: "end",
		sortable: false,
	},
];

interface TableOptions {
	page: number;
	itemsPerPage: number;
	sortBy: { key: string; order: "asc" | "desc" }[];
	search?: string;
}

const loading: Ref<boolean | string> = ref(false);
const total: Ref<number> = ref(0);
const items: Ref<RoleListViewDTO[] | []> = ref([]);
const itemsPerPage: Ref<number> = ref(10);

const loadItems = debounce(async (options: TableOptions) => {
	tableOptions.value = options;
	loading.value = "secondary";

	const { page, itemsPerPage: per_page, sortBy, search } = options;
	const sort = sortBy[0]
		? (sortBy[0]["order"] === "desc" ? "-" : "") + sortBy[0]["key"]
		: undefined;

	const response = await useApiClient("roles").getRoleListViews({
		page,
		per_page,
		sort,
		"filter[name]": search,
	});
	if (response.isOk()) {
		items.value = response.value.data;
		total.value = response.value.total;

		loading.value = false;
	}
}, 300);

const removeRole = async (uuid: string) => {
	const confirm = await useDialog().confirm({
		title: "Delete Role",
		message: `Are you sure you want to delete this role?`,
		isDelete: true,
	});

	if (!confirm) return;

	loading.value = "secondary";
	const response = await toastApiResult(useApiClient("roles").removeRole(uuid));
	if (response.isOk()) {
		await loadItems(tableOptions.value);
		loading.value = false;
	}
};

const props = defineProps<{
	search: string;
}>();

const tableOptions = ref<TableOptions>({
	page: 1,
	itemsPerPage: 10,
	sortBy: [],
	search: "",
});
</script>

<template>
	<v-data-table-server
		:headers="headers"
		:items="items"
		:items-per-page="itemsPerPage"
		:items-length="total"
		@update:options="loadItems"
		class="elevation-0"
		:multi-sort="false"
		:loading="loading"
		:search="props.search"
	>
		<template v-slot:item.name="{ item }">
			<div class="d-flex align-center my-1 font-weight-bold">
				{{ item.name }}
			</div>
		</template>
		<!--			<i class="fa-solid "></i>-->
		<template v-slot:item.actions="{ item }">
			<v-btn
				size="30"
				color="primary"
				variant="text"
				:to="`/roles/edit/${item.uuid}`"
			>
				<i class="fa-light fa-pencil text-primary"></i
			></v-btn>
			<v-btn
				size="30"
				@click="removeRole(item.uuid)"
				color="primary"
				variant="text"
			>
				<i class="fa-light fa-trash-can text-error"></i
			></v-btn>
		</template>
	</v-data-table-server>
</template>

<style scoped></style>
